import { RootState } from '@state/ducks';
import ProductRow from '@stories/Elements/Cards/ProductRow/ProductRow';
import ProductRowSkeleton from '@stories/Elements/Cards/ProductRow/ProductRowSkeleton';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ProductBreakdownAccordionStyles as S } from './ProductBreakdownAccordion.styles';

import { withMotion } from '@hoc/withMotion';
import Minus from '@img/icons/minus.svg';
import Plus from '@img/icons/plus.svg';

export interface ProductBreakdownAccordionProps {
  translations: Record<string, string | undefined>;
}

type ProductCategory = {
  title: string;
  products: IncludedProduct[];
};

const AccordionSection: React.FC<{
  category: ProductCategory;
  translations: Record<string, string | undefined>;
  active: boolean;
  setActive: () => void;
}> = ({ category, translations, active, setActive }) => {
  return (
    <S.CollapsibleContainer>
      <S.CollapsibleHeader onClick={() => setActive()}>
        <span>
          <span>{category.title}</span>
          <span>({category.products.length})</span>
        </span>
        <div>{active ? <Minus /> : <Plus />}</div>
      </S.CollapsibleHeader>
      <S.CollapsibleContent data-active={active}>
        <S.CollapsibleContentInner>
          <S.IncludedProductsHeading>
            <span>{translations['custom.product']}</span>
            <span></span>
            <span>{translations['custom.quantity']}</span>
          </S.IncludedProductsHeading>
          {category.products.map((x) => (
            <ProductRow key={`includedProduct-${x.title}-${x.agilityId}`} {...x} />
          ))}
        </S.CollapsibleContentInner>
      </S.CollapsibleContent>
    </S.CollapsibleContainer>
  );
};

const ProductBreakdownAccordion: React.FC<ProductBreakdownAccordionProps> = (props) => {
  const [activeDropdown, setActiveDropdown] = React.useState<number>(-1);
  const loadingIncludedProducts = useSelector((x: RootState) => x.productDetail.loadingIncludedProducts);
  const includedProducts = useSelector((x: RootState) => x.productDetail.includedProducts);
  const productCategories = useMemo<ProductCategory[]>(() => {
    if (includedProducts && includedProducts.length >= 7) {
      const categories: ProductCategory[] = [];
      includedProducts.forEach((x) => {
        const category = categories.find((c) => c.title === x.categoryTitle);
        if (category) {
          category.products.push(x);
        } else {
          categories.push({
            title: x.categoryTitle ?? '',
            products: [x],
          });
        }
      });

      return categories;
    }

    return [] as ProductCategory[];
  }, [includedProducts]);

  if (!includedProducts || includedProducts.length === 0) {
    return null;
  }

  return (
    <S.Container>
      <S.IncludedProducts>
        {loadingIncludedProducts ? (
          <>
            <ProductRowSkeleton />
            <ProductRowSkeleton />
            <ProductRowSkeleton />
            <ProductRowSkeleton />
          </>
        ) : (
          <>
            {includedProducts.length >= 7 &&
              productCategories.length > 0 &&
              productCategories.map((category, i) => (
                <AccordionSection
                  active={i === activeDropdown}
                  setActive={() => setActiveDropdown(i === activeDropdown ? -1 : i)}
                  key={`category-${i}`}
                  category={category}
                  translations={props.translations}
                />
              ))}
            {includedProducts.length < 7 && (
              <>
                <S.IncludedProductsHeading>
                  <span>{props.translations['custom.product']}</span>
                  <span></span>
                  <span>{props.translations['custom.quantity']}</span>
                </S.IncludedProductsHeading>
                {includedProducts.map((x) => (
                  <ProductRow key={`includedProduct-${x.title}-${x.agilityId}`} {...x} />
                ))}
              </>
            )}
          </>
        )}
      </S.IncludedProducts>
    </S.Container>
  );
};

export default withMotion(ProductBreakdownAccordion);
